import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  ChevronRight,
  LayoutGrid,
  List,
  Search,
  ArrowUpDown,
  Layers,
  User,
  Calendar,
  Shield,
  ChevronDown,
  Loader2,
  FileText,
} from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { usePatronLabels } from '../../contexts/FeatureFlagContext'
import { useAuth } from '../../contexts/AuthContext'
import InvestigationAuditTrailView from './InvestigationAuditTrailView'
import HelpScreenButton from '../../components/help/HelpScreenButton'
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel'
import { buildInvestigationWorkspaceCasesQuery, INVESTIGATION_WORKSPACE_PAGE_SIZE } from '../../config/investigationWorkspace'
import { STAGES } from './investigationPipelineConfig'
import {
  getWorkspaceStageId,
  resolveStageLabel,
  getWorkspaceStageLabel,
  resolvePatronListTitle,
  resolvePatronListSecondaryId,
  dedupeInvestigationCasesOnePerMemberId,
} from '../../utils/workspaceCaseStage'
import { pickStageEnteredAt, getStageStaleInfo, stageAgingTooltip } from './investigationPipelineConfig'

const LIST_HELP_SCREEN_KEY = 'page:compliance-safe/investigations'

const P = {
  pri: '#0a5169',
  priM: '#e8f2f6',
  ink: '#0f172a',
  slate: '#64748b',
  slateL: '#94a3b8',
  border: '#e2e8f0',
  cardBg: '#ffffff',
  pageBg: '#f6f9fb',
  red: '#dc2626',
  amber: '#d97706',
  green: '#16a34a',
  purple: '#7c3aed',
}

const STAGE_GROUP_ORDER = [
  'investigation',
  'pending_evidence',
  'suspicion_formed',
  'smr_draft',
  'under_review',
  'filed',
  'closed',
]

const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest first' },
  { id: 'oldest', label: 'Oldest first' },
  { id: 'progress_high', label: 'Step progress (high → low)' },
  { id: 'progress_low', label: 'Step progress (low → high)' },
  { id: 'steps_remaining', label: 'Most steps remaining' },
  { id: 'steps_remaining_asc', label: 'Fewest steps remaining' },
  { id: 'member_az', label: 'Member A–Z' },
  { id: 'member_za', label: 'Member Z–A' },
  { id: 'severity', label: 'Severity (high first)' },
]

const GROUP_OPTIONS = [
  { id: 'stage', label: 'By pipeline stage' },
  { id: 'case_type', label: 'By case type' },
  { id: 'status', label: 'By status' },
  { id: 'none', label: 'No grouping (flat list)' },
]

const VIEW_MODE_STORAGE_KEY = 'investigation-workspace-view'
const FILTER_DEBOUNCE_MS = 450
const VISIBILITY_REFRESH_MS = 120_000

function readStoredViewMode() {
  try {
    const v = localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    if (v === 'card' || v === 'audit') return v
    return 'list'
  } catch {
    return 'list'
  }
}

function severityRank(s) {
  const u = String(s || '').toUpperCase()
  if (u === 'CRITICAL') return 4
  if (u === 'HIGH') return 3
  if (u === 'MEDIUM') return 2
  if (u === 'LOW') return 1
  return 0
}

function stepsRemaining(c) {
  const t = Number(c.steps_total) || 0
  const d = Number(c.steps_done) || 0
  return Math.max(0, t - d)
}

function sortCases(list, sortBy) {
  const copy = [...list]
  const byDate = (a, b, asc) => {
    const ta = new Date(a.created_at || 0).getTime()
    const tb = new Date(b.created_at || 0).getTime()
    return asc ? ta - tb : tb - ta
  }
  switch (sortBy) {
    case 'newest':
      copy.sort((a, b) => byDate(a, b, false))
      break
    case 'oldest':
      copy.sort((a, b) => byDate(a, b, true))
      break
    case 'progress_high':
      copy.sort((a, b) => (Number(b.progress) || 0) - (Number(a.progress) || 0))
      break
    case 'progress_low':
      copy.sort((a, b) => (Number(a.progress) || 0) - (Number(b.progress) || 0))
      break
    case 'steps_remaining':
      copy.sort((a, b) => stepsRemaining(b) - stepsRemaining(a))
      break
    case 'steps_remaining_asc':
      copy.sort((a, b) => stepsRemaining(a) - stepsRemaining(b))
      break
    case 'member_az':
      copy.sort((a, b) => {
        const na = resolvePatronListTitle(a)
        const nb = resolvePatronListTitle(b)
        const cmp = na.localeCompare(nb, undefined, { sensitivity: 'base' })
        if (cmp !== 0) return cmp
        return String(a.case_number || a.id || '').localeCompare(String(b.case_number || b.id || ''), undefined, {
          sensitivity: 'base',
        })
      })
      break
    case 'member_za':
      copy.sort((a, b) => {
        const na = resolvePatronListTitle(a)
        const nb = resolvePatronListTitle(b)
        const cmp = nb.localeCompare(na, undefined, { sensitivity: 'base' })
        if (cmp !== 0) return cmp
        return String(b.case_number || b.id || '').localeCompare(String(a.case_number || a.id || ''), undefined, {
          sensitivity: 'base',
        })
      })
      break
    case 'severity':
      copy.sort((a, b) => severityRank(b.severity) - severityRank(a.severity))
      break
    default:
      copy.sort((a, b) => byDate(a, b, false))
  }
  return copy
}

/** Stable DOM id for group scroll targets (URL-safe). */
function workspaceGroupAnchorId(groupKey) {
  const raw = String(groupKey || 'group')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `workspace-group-${raw || 'unknown'}`
}

function groupSortedCases(list, groupBy, stageLabelMap = {}) {
  if (groupBy === 'none') {
    return [{ key: '_all', label: 'All cases', items: list }]
  }
  const buckets = new Map()
  for (const c of list) {
    let key
    let label
    if (groupBy === 'case_type') {
      key = String(c.case_type || 'UNKNOWN').toUpperCase()
      label = key
    } else if (groupBy === 'status') {
      key = String(c.status || 'UNKNOWN').toUpperCase()
      label = key.replace(/_/g, ' ')
    } else if (groupBy === 'stage') {
      key = getWorkspaceStageId(c)
      label = resolveStageLabel(key, stageLabelMap)
    } else {
      key = '_all'
      label = 'All'
    }
    if (!buckets.has(key)) buckets.set(key, { key, label, items: [] })
    buckets.get(key).items.push(c)
  }
  const arr = Array.from(buckets.values())
  if (groupBy === 'stage') {
    arr.sort((a, b) => {
      const ia = STAGE_GROUP_ORDER.indexOf(a.key)
      const ib = STAGE_GROUP_ORDER.indexOf(b.key)
      const sa = ia === -1 ? 999 : ia
      const sb = ib === -1 ? 999 : ib
      if (sa !== sb) return sa - sb
      return a.label.localeCompare(b.label)
    })
  } else {
    arr.sort((a, b) => a.label.localeCompare(b.label))
  }
  return arr
}

function useCaseRowFields(inv, stageLabels, disambiguatePatronTitle, isClub = true) {
  const caseId = inv.case_number || inv.id
  const basePatronTitle = resolvePatronListTitle(inv)
  const displayName =
    disambiguatePatronTitle && caseId ? `${basePatronTitle} · ${caseId}` : basePatronTitle
  const subId = resolvePatronListSecondaryId(inv, isClub)
  const daysOpen = inv.days_open ?? 0
  const progress = Number(inv.progress) || 0
  const stepsDone = Number(inv.steps_done) || 0
  const stepsTotal = Number(inv.steps_total) || 0
  const stageId = getWorkspaceStageId(inv)
  const stageLabel = getWorkspaceStageLabel(inv, stageLabels)
  const stageEnteredAt = pickStageEnteredAt(inv, stageId, inv.status)
  const stageStale = getStageStaleInfo(stageEnteredAt, stageId)
  const sev = String(inv.severity || '').toUpperCase()
  const sevColor =
    sev === 'CRITICAL' || sev === 'HIGH' ? P.red : sev === 'MEDIUM' ? P.amber : P.slate
  const caseType = String(inv.case_type || '').toUpperCase()
  const showCaseType = caseType && caseType !== 'INVESTIGATION'
  const daysLabel = daysOpen <= 0 ? 'Today' : `${daysOpen}d`
  const contextLine = [inv.rule_name, inv.venue_name || inv.venue_code].filter(Boolean).join(' · ')
  return {
    caseId,
    displayName,
    subId,
    daysOpen,
    daysLabel,
    progress,
    stepsDone,
    stepsTotal,
    stageLabel,
    stageStale,
    stageEnteredAt,
    sev,
    sevColor,
    showCaseType,
    caseType,
    contextLine,
  }
}

function StepsBadge({ stepsDone, stepsTotal, progress }) {
  if (!stepsTotal) return null
  const pct = Number(progress) || Math.round((stepsDone / stepsTotal) * 100)
  const remaining = Math.max(0, stepsTotal - stepsDone)
  const color = pct >= 100 ? P.green : pct >= 40 ? P.pri : P.amber
  return (
    <span
      className="inline-flex items-center text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded shrink-0"
      style={{ background: `${color}14`, color }}
      title={remaining > 0 ? `${remaining} step${remaining === 1 ? '' : 's'} remaining` : 'All steps complete'}
    >
      {stepsDone}/{stepsTotal}
    </span>
  )
}

function StageBadge({ stageLabel, stageStale, stageEnteredAt }) {
  return (
    <span
      className="text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 max-w-[9rem] truncate"
      style={{
        background: stageStale ? '#fef3c7' : '#f1f5f9',
        color: stageStale ? P.amber : P.slate,
      }}
      title={stageStale ? stageAgingTooltip(stageLabel, stageEnteredAt) : stageLabel}
    >
      {stageStale ? `${stageLabel} · ${stageStale.daysInStage}d` : stageLabel}
    </span>
  )
}

function CaseListRow({ inv, onOpen, stageLabels = {}, disambiguatePatronTitle = false }) {
  const { isClub } = usePatronLabels()
  const f = useCaseRowFields(inv, stageLabels, disambiguatePatronTitle, isClub)
  return (
    <button
      type="button"
      onClick={() => onOpen(f.caseId)}
      className="w-full text-left grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1.4fr)_minmax(0,0.85fr)_minmax(0,1fr)_3rem_3.5rem_auto] gap-x-3 gap-y-0.5 items-center px-3 py-2 rounded-lg border transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#0a5169]"
      style={{
        background: f.stageStale ? '#fffbeb' : P.cardBg,
        borderColor: f.stageStale ? `${P.amber}55` : P.border,
      }}
    >
      <div className="min-w-0 sm:col-span-1 col-span-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <User size={13} style={{ color: P.pri, flexShrink: 0 }} />
          <span className="font-semibold text-[13px] truncate" style={{ color: P.ink }}>
            {f.displayName}
          </span>
        </div>
        <div className="font-mono text-[10px] truncate mt-0.5 pl-5" style={{ color: P.slateL }}>
          {[f.subId, f.caseId].filter(Boolean).join(' · ') || '—'}
        </div>
      </div>
      <div className="hidden sm:flex flex-wrap items-center gap-1 min-w-0">
        <StageBadge stageLabel={f.stageLabel} stageStale={f.stageStale} stageEnteredAt={f.stageEnteredAt} />
        {f.showCaseType && (
          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0" style={{ background: P.priM, color: P.pri }}>
            {f.caseType}
          </span>
        )}
        {f.sev && f.sev !== 'LOW' && (
          <span className="text-[10px] font-bold px-1 py-0.5 rounded flex items-center gap-0.5 shrink-0" style={{ color: f.sevColor, background: `${f.sevColor}12` }}>
            <Shield size={9} />
            {f.sev}
          </span>
        )}
      </div>
      <div className="hidden sm:block text-[11px] truncate min-w-0" style={{ color: P.slate }} title={f.contextLine}>
        {f.contextLine || '—'}
      </div>
      <div className="hidden sm:block text-[11px] tabular-nums text-right" style={{ color: P.slateL }} title="Days open">
        {f.daysLabel}
      </div>
      <div className="hidden sm:flex justify-end">
        <StepsBadge stepsDone={f.stepsDone} stepsTotal={f.stepsTotal} progress={f.progress} />
      </div>
      <div className="flex sm:hidden items-center gap-1.5 flex-wrap col-span-1">
        <StageBadge stageLabel={f.stageLabel} stageStale={f.stageStale} stageEnteredAt={f.stageEnteredAt} />
        <StepsBadge stepsDone={f.stepsDone} stepsTotal={f.stepsTotal} progress={f.progress} />
        <span className="text-[10px]" style={{ color: P.slateL }}>{f.daysLabel}</span>
      </div>
      <ChevronRight size={16} style={{ color: P.slateL, flexShrink: 0 }} className="hidden sm:block" />
    </button>
  )
}

function CaseCard({ inv, onOpen, stageLabels = {}, disambiguatePatronTitle = false }) {
  const { isClub } = usePatronLabels()
  const f = useCaseRowFields(inv, stageLabels, disambiguatePatronTitle, isClub)
  return (
    <button
      type="button"
      onClick={() => onOpen(f.caseId)}
      className="w-full text-left rounded-lg border transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#0a5169] px-3.5 py-2.5"
      style={{
        background: f.stageStale ? '#fffbeb' : P.cardBg,
        borderColor: f.stageStale ? `${P.amber}55` : P.border,
      }}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <User size={14} style={{ color: P.pri, flexShrink: 0 }} />
            <span className="font-semibold text-[13px] truncate" style={{ color: P.ink }}>
              {f.displayName}
            </span>
            <StepsBadge stepsDone={f.stepsDone} stepsTotal={f.stepsTotal} progress={f.progress} />
          </div>
          <div className="font-mono text-[10px] mt-0.5 truncate pl-5" style={{ color: P.slateL }}>
            {[f.subId, f.caseId].filter(Boolean).join(' · ') || '—'}
          </div>
          <div className="flex flex-wrap items-center gap-1 mt-1.5 pl-5">
            <StageBadge stageLabel={f.stageLabel} stageStale={f.stageStale} stageEnteredAt={f.stageEnteredAt} />
            {f.showCaseType && (
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: P.priM, color: P.pri }}>
                {f.caseType}
              </span>
            )}
            {f.sev && f.sev !== 'LOW' && (
              <span className="text-[10px] font-bold px-1 py-0.5 rounded flex items-center gap-0.5" style={{ color: f.sevColor, background: `${f.sevColor}12` }}>
                <Shield size={9} />
                {f.sev}
              </span>
            )}
            {f.contextLine && (
              <span className="text-[10px] truncate max-w-[14rem]" style={{ color: P.slate }} title={f.contextLine}>
                {f.contextLine}
              </span>
            )}
            <span className="text-[10px] inline-flex items-center gap-0.5" style={{ color: P.slateL }}>
              <Calendar size={10} />
              {f.daysOpen <= 0 ? 'Today' : `${f.daysOpen}d open`}
            </span>
          </div>
        </div>
        <ChevronRight size={16} style={{ color: P.slateL, flexShrink: 0, marginTop: 2 }} />
      </div>
    </button>
  )
}

export default function InvestigationList() {
  const labels = usePatronLabels()
  const { can } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [investigations, setInvestigations] = useState([])
  const [paginationInfo, setPaginationInfo] = useState({ total: 0, page: 1, totalPages: 1 })
  const [search, setSearch] = useState('')
  const [patronFilter, setPatronFilter] = useState('')
  const [memberFilter, setMemberFilter] = useState('')
  const [visitorFilter, setVisitorFilter] = useState('')
  const [subjectTypeFilter, setSubjectTypeFilter] = useState('')
  const [appliedFilters, setAppliedFilters] = useState({
    patron_id: '',
    member_id: '',
    visitor_id: '',
    subject_type: '',
  })
  const [sortBy, setSortBy] = useState('newest')
  const [groupBy, setGroupBy] = useState('stage')
  const [stageFilter, setStageFilter] = useState('')
  const [viewMode, setViewMode] = useState(readStoredViewMode)
  const [stageLabels, setStageLabels] = useState({})
  const [helpPanelOpen, setHelpPanelOpen] = useState(false)
  const hasLoadedOnceRef = useRef(false)
  const lastFetchedAtRef = useRef(0)
  const fetchGenRef = useRef(0)

  useEffect(() => {
    const t = window.setTimeout(() => {
      setAppliedFilters({
        patron_id: patronFilter.trim(),
        member_id: memberFilter.trim(),
        visitor_id: visitorFilter.trim(),
        subject_type: subjectTypeFilter.trim(),
      })
    }, FILTER_DEBOUNCE_MS)
    return () => window.clearTimeout(t)
  }, [patronFilter, memberFilter, visitorFilter, subjectTypeFilter])

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode)
    } catch {
      /* ignore */
    }
  }, [viewMode])

  const fetchPage = useCallback(async (page, append = false) => {
    const gen = ++fetchGenRef.current
    const isInitial = !hasLoadedOnceRef.current && !append
    try {
      if (append) setLoadingMore(true)
      else if (isInitial) {
        setLoading(true)
        setError(null)
      } else {
        setRefreshing(true)
      }
      const qs = buildInvestigationWorkspaceCasesQuery({
        ...appliedFilters,
        pipeline_stage: stageFilter,
        page,
        limit: INVESTIGATION_WORKSPACE_PAGE_SIZE,
      })
      const res = await authFetch(getLocalApiUrl(`/api/cases?${qs}`))
      const json = await res.json()
      if (gen !== fetchGenRef.current) return
      if (!json.success) throw new Error(json.error || 'Failed to fetch investigations')
      const incoming = json.data || []
      setInvestigations((prev) => (append ? [...prev, ...incoming] : incoming))
      setPaginationInfo({
        total: json.pagination?.total ?? incoming.length,
        page: json.pagination?.page ?? page,
        totalPages: json.pagination?.totalPages ?? 1,
      })
      lastFetchedAtRef.current = Date.now()
      hasLoadedOnceRef.current = true
    } catch (e) {
      if (gen !== fetchGenRef.current) return
      if (!append) setError(e.message || 'Failed to load investigations')
    } finally {
      if (gen !== fetchGenRef.current) return
      setLoading(false)
      setRefreshing(false)
      setLoadingMore(false)
    }
  }, [appliedFilters, stageFilter])

  useEffect(() => {
    fetchPage(1, false)
  }, [fetchPage])

  useEffect(() => {
    let visTimer = null
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      if (!hasLoadedOnceRef.current) return
      const age = Date.now() - lastFetchedAtRef.current
      if (age < VISIBILITY_REFRESH_MS) return
      clearTimeout(visTimer)
      visTimer = setTimeout(() => fetchPage(1, false), 400)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearTimeout(visTimer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [fetchPage])

  const handleLoadMore = useCallback(() => {
    const nextPage = paginationInfo.page + 1
    if (nextPage <= paginationInfo.totalPages && !loadingMore) {
      fetchPage(nextPage, true)
    }
  }, [paginationInfo, loadingMore, fetchPage])

  // Same source as Investigation Pipeline: Pipeline → INVESTIGATION_STAGE_LABELS
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/configuration'))
        const json = await res.json()
        if (cancelled || !json.success) return
        const pipeline = (json.data || []).find((c) => c.category === 'Pipeline')
        const setting = pipeline?.settings?.find((s) => s.name === 'INVESTIGATION_STAGE_LABELS')
        if (setting?.value) {
          try {
            const labels = JSON.parse(setting.value)
            if (labels && typeof labels === 'object') setStageLabels(labels)
          } catch {
            /* keep defaults */
          }
        }
      } catch {
        /* keep defaults */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return investigations.filter((c) => {
      if (stageFilter && getWorkspaceStageId(c) !== stageFilter) return false
      if (!q) return true
      const id = `${c.case_number || ''} ${c.id || ''}`.toLowerCase()
      const name = `${c.member_name || ''} ${c.member_id || ''} ${c.patron_id || ''} ${c.master_profile_id || ''} ${c.visitor_id || ''} ${resolvePatronListTitle(c)}`.toLowerCase()
      const rule = `${c.rule_name || ''} ${c.rule_id || ''}`.toLowerCase()
      const venue = `${c.venue_name || ''} ${c.venue_code || ''}`.toLowerCase()
      return id.includes(q) || name.includes(q) || rule.includes(q) || venue.includes(q)
    })
  }, [investigations, search, stageFilter])

  const sorted = useMemo(() => sortCases(filtered, sortBy), [filtered, sortBy])
  const workspaceCases = useMemo(
    () => dedupeInvestigationCasesOnePerMemberId(sorted),
    [sorted]
  )

  /** Same patron title on multiple visible rows — append case id on the title line so cards are distinct. */
  const patronTitleDupKeys = useMemo(() => {
    const counts = new Map()
    for (const inv of workspaceCases) {
      const t = resolvePatronListTitle(inv)
      const k = String(t).trim().toLowerCase() || '\0'
      counts.set(k, (counts.get(k) || 0) + 1)
    }
    const dups = new Set()
    for (const [k, n] of counts.entries()) {
      if (n > 1) dups.add(k)
    }
    return dups
  }, [workspaceCases])

  const grouped = useMemo(() => groupSortedCases(workspaceCases, groupBy, stageLabels), [workspaceCases, groupBy, stageLabels])

  const openCase = (caseId) => navigate(`/compliance-safe/investigations/${encodeURIComponent(caseId)}`)

  const scrollToGroup = useCallback((groupKey) => {
    const id = workspaceGroupAnchorId(groupKey)
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      try {
        history.replaceState(null, '', `#${id}`)
      } catch {
        /* ignore */
      }
    }
  }, [])

  // Deep link: URL hash #workspace-group-… scrolls to that section after render
  useEffect(() => {
    if (loading || workspaceCases.length === 0 || groupBy === 'none') return
    const hash = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : ''
    if (!hash || !hash.startsWith('workspace-group-')) return
    const t = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 120)
    return () => window.clearTimeout(t)
  }, [loading, workspaceCases.length, groupBy])

  const showGroupNav = viewMode !== 'audit' && groupBy !== 'none' && workspaceCases.length > 0
  const isAuditView = viewMode === 'audit'
  const canExportAudit = can('compliance-safe/investigations/pipeline', 'manage')
    || can('compliance-safe/investigations', 'manage')
    || can('compliance-safe/cases', 'manage')
    || can('cases', 'manage')
  const auditCaseIds = useMemo(
    () => workspaceCases.map((c) => c.id).filter(Boolean),
    [workspaceCases]
  )
  const auditExportQuery = useMemo(
    () => buildInvestigationWorkspaceCasesQuery({
      ...appliedFilters,
      pipeline_stage: stageFilter,
      page: 1,
      limit: 2000,
    }).toString(),
    [appliedFilters, stageFilter]
  )

  const CaseItem = viewMode === 'card' ? CaseCard : CaseListRow
  const listGap = viewMode === 'card' ? 'space-y-2' : 'space-y-1'

  if (loading && investigations.length === 0) {
    return (
      <div className="min-h-full p-6" style={{ background: P.pageBg }}>
        <div className="max-w-5xl mx-auto rounded-xl border p-10 text-center text-sm" style={{ borderColor: P.border, background: P.cardBg, color: P.slate }}>
          Loading workspace…
        </div>
      </div>
    )
  }

  if (error && investigations.length === 0) {
    return (
      <div className="min-h-full p-6" style={{ background: P.pageBg }}>
        <div className="max-w-5xl mx-auto rounded-xl border p-10 text-sm" style={{ borderColor: P.border, background: P.cardBg, color: P.red }}>
          {error}
        </div>
      </div>
    )
  }

  const GroupNavLinks = ({ className = '', compact = false }) => (
    <nav className={className} aria-label="Jump to group">
      <ul className="list-none m-0 p-0 space-y-0.5">
        {grouped.map((group) => {
          const anchor = workspaceGroupAnchorId(group.key)
          return (
            <li key={group.key}>
              <button
                type="button"
                onClick={() => scrollToGroup(group.key)}
                className={`w-full text-left rounded-lg border border-transparent transition-colors hover:bg-white/80 ${compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'}`}
                style={{ color: P.ink }}
              >
                <span className="font-semibold block truncate" title={group.label}>
                  {group.label}
                </span>
                <span className="text-[11px] font-bold tabular-nums" style={{ color: P.slateL }}>
                  {group.items.length} case{group.items.length === 1 ? '' : 's'}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )

  return (
    <div className="min-h-full p-6" style={{ background: P.pageBg }}>
      <div className={`mx-auto space-y-5 ${isAuditView ? 'max-w-none' : showGroupNav ? 'max-w-6xl xl:max-w-7xl' : 'max-w-5xl'}`}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-extrabold tracking-tight m-0" style={{ color: P.ink }}>
              Investigation workspace
            </h1>
            <p className="text-sm m-0 mt-1.5 leading-relaxed" style={{ color: P.slate }}>
              {isAuditView
                ? 'Stage audit trail — who moved each investigation, the note they typed, and the checklist sentences they ticked. Export as Excel when you have pipeline manage.'
                : 'Same cases as the pipeline, grouped by stage with quick navigation. Stage names follow your Pipeline settings. Sort or change grouping below if you need a different view.'}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <HelpScreenButton
              screenKey={LIST_HELP_SCREEN_KEY}
              onClick={() => setHelpPanelOpen(true)}
            />
            <div
              className="inline-flex rounded-lg border overflow-hidden"
              style={{ borderColor: P.border }}
              role="group"
              aria-label="View mode"
            >
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 transition-colors"
                style={{
                  background: viewMode === 'list' ? P.priM : P.cardBg,
                  color: viewMode === 'list' ? P.pri : P.slate,
                }}
                title="Compact list"
              >
                <List size={14} />
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 border-l transition-colors"
                style={{
                  borderColor: P.border,
                  background: viewMode === 'card' ? P.priM : P.cardBg,
                  color: viewMode === 'card' ? P.pri : P.slate,
                }}
                title="Card view"
              >
                <LayoutGrid size={14} />
                Cards
              </button>
              <button
                type="button"
                onClick={() => setViewMode('audit')}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 border-l transition-colors"
                style={{
                  borderColor: P.border,
                  background: viewMode === 'audit' ? P.priM : P.cardBg,
                  color: viewMode === 'audit' ? P.pri : P.slate,
                }}
                title="Stage audit trail"
              >
                <FileText size={14} />
                Audit trail
              </button>
            </div>
            <Link
              to="/compliance-safe/investigations/pipeline"
              className="inline-flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-lg border no-underline transition-colors hover:opacity-90"
              style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
            >
              <LayoutGrid size={16} />
              Pipeline
            </Link>
          </div>
        </div>

        <div className="rounded-xl border p-4 sm:p-5 space-y-4" style={{ borderColor: P.border, background: P.cardBg }}>
          <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
            <div className="flex-1 min-w-0">
              <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color: P.slateL }}>
                Search
              </label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: P.slateL }} />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Patron, member, visitor, case #, rule, venue…"
                  className="w-full rounded-lg border pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2"
                  style={{ borderColor: P.border, color: P.ink }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1 min-w-0">
              <input
                type="text"
                value={patronFilter}
                onChange={(e) => setPatronFilter(e.target.value)}
                placeholder="Patron ID (PAT-…)"
                className="rounded-lg border px-3 py-2.5 text-sm font-mono"
                style={{ borderColor: P.border, color: P.ink }}
              />
              <input
                type="text"
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
                placeholder="Member #"
                className="rounded-lg border px-3 py-2.5 text-sm font-mono"
                style={{ borderColor: P.border, color: P.ink }}
              />
              <input
                type="text"
                value={visitorFilter}
                onChange={(e) => setVisitorFilter(e.target.value)}
                placeholder={labels.visitorId}
                className="rounded-lg border px-3 py-2.5 text-sm font-mono"
                style={{ borderColor: P.border, color: P.ink }}
              />
              <select
                value={subjectTypeFilter}
                onChange={(e) => setSubjectTypeFilter(e.target.value)}
                className="rounded-lg border px-3 py-2.5 text-sm bg-white"
                style={{ borderColor: P.border, color: P.ink }}
              >
                <option value="">All subjects</option>
                <option value="member">Member</option>
                <option value="visitor">{labels.visitor}</option>
              </select>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color: P.slateL }}>
                  Pipeline stage
                </label>
                <select
                  value={stageFilter}
                  onChange={(e) => setStageFilter(e.target.value)}
                  className="rounded-lg border px-3 py-2.5 text-sm min-w-[200px] cursor-pointer bg-white"
                  style={{ borderColor: P.border, color: P.ink }}
                >
                  <option value="">All stages</option>
                  {STAGES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {resolveStageLabel(s.id, stageLabels)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color: P.slateL }}>
                  <span className="inline-flex items-center gap-1">
                    <ArrowUpDown size={11} /> Sort
                  </span>
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-lg border px-3 py-2.5 text-sm min-w-[200px] cursor-pointer bg-white"
                  style={{ borderColor: P.border, color: P.ink }}
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              {!isAuditView && (
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color: P.slateL }}>
                  <span className="inline-flex items-center gap-1">
                    <Layers size={11} /> Group by
                  </span>
                </label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="rounded-lg border px-3 py-2.5 text-sm min-w-[200px] cursor-pointer bg-white"
                  style={{ borderColor: P.border, color: P.ink }}
                >
                  {GROUP_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="text-xs font-semibold flex items-center gap-2" style={{ color: P.slate }}>
              {refreshing && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: P.pri }}>
                  <Loader2 size={12} className="animate-spin" />
                  Updating…
                </span>
              )}
              Showing <strong style={{ color: P.ink }}>{workspaceCases.length}</strong>
              {filtered.length !== investigations.length ? (
                <> of <strong style={{ color: P.ink }}>{filtered.length}</strong> filtered</>
              ) : investigations.length < paginationInfo.total ? (
                <> loaded of <strong style={{ color: P.ink }}>{paginationInfo.total}</strong></>
              ) : null}{' '}
              case{workspaceCases.length === 1 ? '' : 's'}
            </div>
            {investigations.length < paginationInfo.total && (
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors"
                style={{ borderColor: P.border, background: P.priM, color: P.pri, opacity: loadingMore ? 0.6 : 1 }}
              >
                <ChevronDown size={13} />
                {loadingMore ? 'Loading…' : `Load more (${paginationInfo.total - investigations.length} remaining)`}
              </button>
            )}
          </div>
        </div>

        {workspaceCases.length === 0 ? (
          <div className="rounded-xl border p-12 text-center text-sm" style={{ borderColor: P.border, background: P.cardBg, color: P.slate }}>
            {investigations.length === 0
              ? (stageFilter
                ? 'No cases in this pipeline stage. Choose All stages or another stage.'
                : 'No cases in this workspace yet. New matters appear here when rules create pipeline cases.')
              : 'No cases match your search. Try different keywords or clear the search box.'}
          </div>
        ) : isAuditView ? (
          <div className="space-y-5">
            <InvestigationAuditTrailView
              caseIds={auditCaseIds}
              cases={workspaceCases}
              stageLabels={stageLabels}
              onOpenCase={openCase}
              canExport={canExportAudit}
              exportQuery={auditExportQuery}
            />
            {investigations.length < paginationInfo.total && (
              <div className="text-center py-2">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-xl border transition-colors hover:opacity-90"
                  style={{ borderColor: P.pri, background: P.priM, color: P.pri, opacity: loadingMore ? 0.6 : 1 }}
                >
                  <ChevronDown size={15} />
                  {loadingMore
                    ? 'Loading more cases…'
                    : `Load more — ${paginationInfo.total - investigations.length} cases remaining`}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className={`flex flex-col gap-6 ${showGroupNav ? 'lg:flex-row lg:items-start lg:gap-8' : ''}`}>
            {showGroupNav && (
              <>
                <aside
                  className="hidden lg:block shrink-0 w-52 xl:w-56 sticky top-4 z-10 rounded-xl border p-3"
                  style={{ borderColor: P.border, background: P.cardBg, maxHeight: 'calc(100vh - 5rem)' }}
                >
                  <div className="text-[10px] font-extrabold uppercase tracking-wider mb-2 px-1" style={{ color: P.slateL }}>
                    Jump to group
                  </div>
                  <div className="overflow-y-auto pr-1" style={{ maxHeight: 'calc(100vh - 8rem)' }}>
                    <GroupNavLinks />
                  </div>
                </aside>
                <div
                  className="lg:hidden -mx-1 px-1 pb-1 overflow-x-auto flex gap-2 snap-x snap-mandatory"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {grouped.map((group) => (
                    <button
                      key={group.key}
                      type="button"
                      onClick={() => scrollToGroup(group.key)}
                      className="snap-start shrink-0 rounded-full border px-3 py-2 text-xs font-bold whitespace-nowrap"
                      style={{ borderColor: P.border, background: P.priM, color: P.pri }}
                    >
                      {group.label}
                      <span className="opacity-80 font-semibold ml-1">({group.items.length})</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="flex-1 min-w-0 space-y-5">
              {grouped.map((group) => (
                <section
                  key={group.key}
                  id={groupBy !== 'none' ? workspaceGroupAnchorId(group.key) : undefined}
                  className="scroll-mt-24"
                >
                  {groupBy !== 'none' && (
                    <h2 className="text-sm font-extrabold tracking-tight m-0 mb-2 flex items-center gap-2" style={{ color: P.pri }}>
                      <span>{group.label}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: P.priM, color: P.pri }}>
                        {group.items.length}
                      </span>
                    </h2>
                  )}
                  {viewMode === 'list' && group.items.length > 0 && (
                    <div
                      className="hidden sm:grid grid-cols-[minmax(0,1.4fr)_minmax(0,0.85fr)_minmax(0,1fr)_3rem_3.5rem_auto] gap-x-3 px-3 pb-1 text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: P.slateL }}
                    >
                      <span>Patron / case</span>
                      <span>Stage</span>
                      <span>Rule / venue</span>
                      <span className="text-right">Age</span>
                      <span className="text-right">Steps</span>
                      <span />
                    </div>
                  )}
                  <ul className={`list-none m-0 p-0 ${listGap}`}>
                    {group.items.map((inv) => {
                      const baseTitle = resolvePatronListTitle(inv)
                      const dupKey = String(baseTitle).trim().toLowerCase() || '\0'
                      return (
                        <li key={inv.case_number || inv.id}>
                          <CaseItem
                            inv={inv}
                            onOpen={openCase}
                            stageLabels={stageLabels}
                            disambiguatePatronTitle={patronTitleDupKeys.has(dupKey)}
                          />
                        </li>
                      )
                    })}
                  </ul>
                </section>
              ))}

              {investigations.length < paginationInfo.total && (
                <div className="text-center py-6">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-xl border transition-colors hover:opacity-90"
                    style={{ borderColor: P.pri, background: P.priM, color: P.pri, opacity: loadingMore ? 0.6 : 1 }}
                  >
                    <ChevronDown size={15} />
                    {loadingMore
                      ? 'Loading more cases…'
                      : `Load more — ${paginationInfo.total - investigations.length} cases remaining`}
                  </button>
                  <p className="text-xs mt-2" style={{ color: P.slateL }}>
                    Showing {investigations.length} of {paginationInfo.total} total
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ScreenHelpPanel
        screenKey={LIST_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        sectionIdPrefix="investigation-list-help"
      />
    </div>
  )
}
